import type { Dayjs } from 'dayjs';
import type { MediaModel } from 'src/types/media';
import type { UseFormReturn } from 'react-hook-form';

import dayjs from 'dayjs';
import { useState } from 'react';

import { toast } from 'src/components/snackbar';

import { buildOrderItemFromForm } from '../utils/order-mappers';

import type { OrderItem, NewOrderSchemaType } from '../types/order-form.types';

export function useOrderItems(methods: UseFormReturn<NewOrderSchemaType>, medias: MediaModel[]) {
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [editingItemIndex, setEditingItemIndex] = useState<number>(-1);

  const { reset, setValue, handleSubmit } = methods;

  const clearAutoCompleteField = (fieldName: string) => {
    const element = document.querySelector(
      `#rhf-autocomplete-${fieldName} input`
    ) as HTMLInputElement;
    if (element) element.value = '';
  };

  const resetForm = (preserveLimitDate?: Dayjs) => {
    reset(
      {
        mediaId: undefined,
        price: 0,
        currency: 0,
        limitDate: preserveLimitDate || dayjs().add(7, 'day'),
        categoryId: null,
        categoryCommission: 0,
        finalUrl: '',
        requireRedaction: false,
        extraRedaction: false,
        wordCount: 600,
        orderDetail: '',
        extraTermsToAvoid: '',
        title: '',
        extraHtml: '',
        extraSource: '',
        image: null,
        imageTwo: null,
        url1: '',
        keyword1: '',
        linkType1: undefined,
        url2: '',
        keyword2: '',
        linkType2: undefined,
        url3: '',
        keyword3: '',
        linkType3: undefined,
        url4: '',
        keyword4: '',
        linkType4: undefined,
        file: null,
        costPrice: undefined,
        costPriceCurrency: 0,
        internalPlatformId: '',
        discountPercentage: undefined,
        directProfit: undefined,
        redactionUrl: '',
      },
      {
        keepDirty: false,
        keepTouched: false,
        keepIsSubmitted: false,
        keepErrors: false,
        keepIsValid: false,
        keepDefaultValues: false,
      }
    );

    setTimeout(() => {
      setValue('mediaId', null, { shouldValidate: true });
      clearAutoCompleteField('mediaId');
    }, 0);
  };

  const onAddOrUpdateItem = handleSubmit(async (data) => {
    try {
      const newItem = await buildOrderItemFromForm(data, medias);

      if (editingItemIndex >= 0) {
        setOrderItems((prev) => {
          const updated = [...prev];
          updated[editingItemIndex] = newItem;
          return updated;
        });
        setEditingItemIndex(-1);
        toast.success('Item actualizado correctamente');
      } else {
        setOrderItems((prev) => [...prev, newItem]);
        toast.success('Medio anadido al carrito correctamente');
      }

      resetForm(data.limitDate);
    } catch (error) {
      console.error(error);
      toast.error('Error al anadir el item al pedido');
    }
  });

  const handleRemoveItem = (item: OrderItem) => {
    setOrderItems((prev) => prev.filter((i) => i !== item));
    setEditingItemIndex(-1);
  };

  const handleEditItem = (index: number) => {
    const item = orderItems[index];
    if (!item) return;

    setEditingItemIndex(index);

    const mediaObject = medias?.find((m) => m.id === item.mediaId);
    if (item.mediaId && mediaObject) {
      setValue('mediaId', { value: mediaObject.id, label: mediaObject.name });
    }

    setValue('price', item.price);
    setValue('currency', item.currency);
    setValue('finalUrl', item.finalUrl || '');
    setValue('wordCount', item.wordCount || 600);
    setValue('costPrice', item.costPrice);
    setValue('costPriceCurrency', item.costPriceCurrency || 0);
    setValue('categoryCommission', item.categoryCommission || 0);

    if (item.limitDate) setValue('limitDate', dayjs(item.limitDate));
    if (item.categoryId) {
      setTimeout(() => {
        setValue('categoryId', item.categoryId!, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
      }, 300);
    }

    setValue('requireRedaction', item.requireRedaction || false);
    setValue('orderDetail', item.orderDetail || '');
    setValue('title', item.title || '');
    setValue('extraTermsToAvoid', item.extraTermsToAvoid || '');
    setValue('extraHtml', item.extraHtml || '');
    setValue('extraSource', item.extraSource || '');
    setValue('url1', item.url1 || '');
    setValue('keyword1', item.keyword1 || '');
    setValue('linkType1', item.linkType1);
    setValue('url2', item.url2 || '');
    setValue('keyword2', item.keyword2 || '');
    setValue('linkType2', item.linkType2);
    setValue('url3', item.url3 || '');
    setValue('keyword3', item.keyword3 || '');
    setValue('linkType3', item.linkType3);
    setValue('url4', item.url4 || '');
    setValue('keyword4', item.keyword4 || '');
    setValue('linkType4', item.linkType4);
    setValue('internalPlatformId', item.internalPlatformId || '');
    setValue('discountPercentage', item.discountPercentage);
    setValue('directProfit', item.directProfit);
    setValue('redactionUrl', item.redactionUrl || '');

    toast.info(
      'Editando item del pedido. Modifica los campos y haz clic en "Guardar Cambios".'
    );
  };

  const handleCancelEdit = () => {
    setEditingItemIndex(-1);
    resetForm();
    toast.info('Edicion cancelada');
  };

  // Aplica un descuento global (%) a TODOS los items del pedido (sobrescribe el descuento individual).
  // Se mantiene poder editar cada item por separado después.
  const applyGlobalDiscount = (percentage: number) => {
    const pct = Math.max(0, Math.min(100, Number(percentage) || 0));
    if (orderItems.length === 0) {
      toast.info('Añade medios antes de aplicar un descuento global.');
      return;
    }
    setOrderItems((prev) => prev.map((item) => ({ ...item, discountPercentage: pct })));
    // Si se está editando un item, refleja también el cambio en el formulario.
    if (editingItemIndex >= 0) setValue('discountPercentage', pct);
    toast.success(
      pct > 0
        ? `Descuento del ${pct}% aplicado a ${orderItems.length} medio(s).`
        : `Descuento quitado de ${orderItems.length} medio(s).`
    );
  };

  return {
    orderItems,
    setOrderItems,
    editingItemIndex,
    onAddOrUpdateItem,
    handleRemoveItem,
    handleEditItem,
    handleCancelEdit,
    applyGlobalDiscount,
  };
}
